import { createFileRoute } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listStrandedInventory } from "@/lib/stranded-inventory.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, PackageX } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/inventory/stranded")({
  component: StrandedInventory,
});

function StrandedInventory() {
  const { tenantId, facilityId, customerId } = useScope();
  const fn = useServerFn(listStrandedInventory);

  const { data, isLoading } = useQuery({
    queryKey: ["stranded-inventory", tenantId, facilityId, customerId],
    enabled: !!tenantId,
    queryFn: () => fn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null, customerId: customerId ?? null, limit: 200 } }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];

  return (
    <>
      <PageHeader
        title="Stranded Inventory"
        description="SKUs whose on-hand is parked exclusively in non-pickable, non-allocatable, or inactive locations. The allocator skips these — replenish them forward or fix the location class."
      />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <PackageX className="h-4 w-4" />Stranded SKUs
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold tabular-nums">{rows.length}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />Units unavailable
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold tabular-nums">
              {(data?.total_stranded_units ?? 0).toLocaleString()}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Affected SKUs</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Stranded qty</TableHead>
                  <TableHead>Locations</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">All inventory is reachable by the allocator.</TableCell></TableRow>
                ) : rows.map((r: any) => (
                  <TableRow key={r.sku_id}>
                    <TableCell className="font-mono text-xs">{r.sku_code}</TableCell>
                    <TableCell className="text-sm">{r.sku_name}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{r.stranded_qty.toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.stranded_locations.slice(0, 6).map((l: any) => (
                          <Badge key={l.id} variant="outline" className="font-mono text-[10px]" title={`${l.reason} · ${l.qty} units`}>
                            {l.code} · {l.qty}
                          </Badge>
                        ))}
                        {r.stranded_locations.length > 6 && (
                          <Badge variant="outline" className="text-[10px]">+{r.stranded_locations.length - 6} more</Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}